import {
  ILoyaltyPromotionInput,
  IPromotionInput,
  LoyaltyPromotionType,
  LoyaltySwapType,
} from '@fhs-legacy/frontend/src/generated/graphql-gateway';

import type { OfferCardProps } from '../components/offer-card/offer-card';
import { LoyaltyIncentiveOffer } from '../queries/loyalty.queries';

/** Prefer CMS / API `imageAlt`; fall back to title-based label when unset. */
export function offerImageAccessibilityLabel(title: string, imageAlt?: string | null): string {
  const trimmed = imageAlt?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : `${title} offer image`;
}

export const toLoyaltyPromotionInput = ({
  swap,
  type,
  ...rest
}: IPromotionInput): ILoyaltyPromotionInput => ({
  ...rest,
  type: type as string as LoyaltyPromotionType,
  ...(swap && {
    swap: {
      ...swap,
      type: swap.type as string as LoyaltySwapType,
    },
  }),
});

/**
 * Formats an offer's expiry date into a short human-readable label, mirroring
 * the legacy loyalty-service logic. Returns `undefined` when the date is
 * missing, invalid, or more than a year away (effectively "no expiry").
 *
 * Note: the calculation uses the client clock, which can be manipulated.
 * That's acceptable here because the label is purely informational — expired
 * offers are filtered out server-side and won't be redeemable.
 */
export function formatOfferExpiry(expiryDate?: string | null): string | undefined {
  if (!expiryDate) {
    return undefined;
  }

  const expiry = new Date(expiryDate);
  if (Number.isNaN(expiry.getTime())) {
    return undefined;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const expiryDay = new Date(expiry);
  expiryDay.setHours(0, 0, 0, 0);

  const msPerDay = 1000 * 60 * 60 * 24;
  const daysRemaining = Math.round((expiryDay.getTime() - today.getTime()) / msPerDay);

  if (daysRemaining < 0 || daysRemaining > 365) {
    return undefined;
  }

  if (daysRemaining === 0) {
    return 'Expires today';
  }

  if (daysRemaining === 1) {
    return 'Expires in 1 day';
  }

  if (daysRemaining <= 7) {
    return `Expires in ${daysRemaining} days`;
  }

  const month = String(expiry.getMonth() + 1).padStart(2, '0');
  const day = String(expiry.getDate()).padStart(2, '0');
  return `Expires ${month}/${day}`;
}

export function formatOfferCards(offers?: LoyaltyIncentiveOffer[], cardsToRender?: number) {
  return (
    offers?.slice(0, cardsToRender)?.map<OfferCardProps>(offer => ({
      id: offer.id,
      imageUrl: offer.image,
      imageAlt: offer.imageAlt,
      title: offer.name,
      description: offer.description,
      expiry: formatOfferExpiry(offer.expiryDate),
      onPressButton: offer.isApplied ? offer.removeOffer : offer.applyOffer,
      onPressImage: offer.seeDetails,
      onPressTitle: offer.seeDetails,
      tag: offer.tag,
      buttonVariant: offer.isApplied ? 'applied' : 'default',
      buttonLabel: offer.isApplied
        ? 'Offer Applied'
        : offer.isAvailable
          ? 'Apply Offer'
          : 'See Details',
    })) ?? []
  );
}
